import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';

// Font Size Control Icons - Custom SVG icons matching Lucide style (24px)
const FontSizeIncreaseIcon = ({ className = "w-6 h-6" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Large A */}
    <path d="M4 20L8 8l4 12" />
    <path d="M6.5 15h3" />
    {/* Small A */}
    <path d="M15 20l2.5-6 2.5 6" />
    <path d="M16.5 17h2" />
    {/* Plus symbol */}
    <path d="M19 4v4" />
    <path d="M17 6h4" />
  </svg>
);

const FontSizeDecreaseIcon = ({ className = "w-6 h-6" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Large A */}
    <path d="M4 20L8 8l4 12" />
    <path d="M6.5 15h3" />
    {/* Small A */}
    <path d="M15 20l2.5-6 2.5 6" />
    <path d="M16.5 17h2" />
    {/* Minus symbol */}
    <path d="M17 6h4" />
  </svg>
);

// Font size scale options with descriptive labels
const FONT_SCALES = [
  { key: 'xs', multiplier: 0.75, label: '75%', name: 'Extra Small' },
  { key: 'sm', multiplier: 0.875, label: '87.5%', name: 'Small' },
  { key: 'base', multiplier: 1.0, label: '100%', name: 'Normal' },
  { key: 'lg', multiplier: 1.125, label: '112.5%', name: 'Large' },
  { key: 'xl', multiplier: 1.25, label: '125%', name: 'Extra Large' },
  { key: '2xl', multiplier: 1.375, label: '137.5%', name: 'XXL' },
  { key: '3xl', multiplier: 1.5, label: '150%', name: 'XXXL' }
];

const DEFAULT_SCALE = 'base';
const STORAGE_KEY = 'potred-font-scale';

const FontSizeControls = () => {
  const [currentScale, setCurrentScale] = useState(DEFAULT_SCALE);
  const [isVisible, setIsVisible] = useState(false); // Default to hidden
  const [isLoaded, setIsLoaded] = useState(false);

  // Check if we're on a post or story page and load preferences
  useEffect(() => {
    // Check page type - show font controls only on post and story pages
    const isPostPage = document.body.classList.contains('post-template') ||
                      document.body.classList.contains('story-template');

    setIsVisible(isPostPage);

    if (isPostPage) {
      // Load saved font scale preference only on relevant pages
      const savedScale = localStorage.getItem(STORAGE_KEY);
      if (savedScale && FONT_SCALES.some(scale => scale.key === savedScale)) {
        setCurrentScale(savedScale);
      }
    }

    // Mark as loaded to prevent flickering
    setIsLoaded(true);
  }, []);

  // Apply font scale to document
  useEffect(() => {
    const scale = FONT_SCALES.find(s => s.key === currentScale);
    if (scale) {
      // Remove all existing font scale classes
      FONT_SCALES.forEach(s => {
        document.documentElement.classList.remove(`font-scale-${s.key}`);
      });

      // Add current scale class
      document.documentElement.classList.add(`font-scale-${currentScale}`);

      // Update CSS custom property for immediate effect
      document.documentElement.style.setProperty('--font-scale-multiplier', scale.multiplier);

      // Save preference
      localStorage.setItem(STORAGE_KEY, currentScale);
    }
  }, [currentScale]);

  const getCurrentScaleIndex = () => {
    return FONT_SCALES.findIndex(scale => scale.key === currentScale);
  };

  const canDecrease = () => getCurrentScaleIndex() > 0;
  const canIncrease = () => getCurrentScaleIndex() < FONT_SCALES.length - 1;

  const decreaseFontSize = () => {
    const currentIndex = getCurrentScaleIndex();
    if (currentIndex > 0) {
      setCurrentScale(FONT_SCALES[currentIndex - 1].key);
    }
  };

  const increaseFontSize = () => {
    const currentIndex = getCurrentScaleIndex();
    if (currentIndex < FONT_SCALES.length - 1) {
      setCurrentScale(FONT_SCALES[currentIndex + 1].key);
    }
  };

  const resetFontSize = () => {
    setCurrentScale(DEFAULT_SCALE);
  };

  const currentScaleData = FONT_SCALES.find(s => s.key === currentScale);

  if (!isVisible) return null;

  return (
    <div className={`font-controls-container visible ${isLoaded ? 'loaded' : ''}`}>
      {/* Decrease Font Size Button */}
      <motion.button
        onClick={decreaseFontSize}
        disabled={!canDecrease()}
        className="font-control-button"
        aria-label="Decrease font size"
        title={`Decrease font size${!canDecrease() ? ' (minimum reached)' : ''}`}
        whileHover={canDecrease() ? {
          scale: [null, 1.1, 1.6],
          transition: {
            duration: 0.3,
            times: [0, 0.6, 1],
            ease: ["easeInOut", "easeOut"],
          },
        } : {}}
        whileTap={canDecrease() ? {
          scale: 0.95,
          transition: { duration: 0.1 }
        } : {}}
      >
        <FontSizeDecreaseIcon className="w-6 h-6" />
      </motion.button>

      {/* Current Font Size Indicator */}
      <motion.div
        className="font-size-indicator"
        key={currentScale}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        title={`Current size: ${currentScaleData?.name} (${currentScaleData?.label})`}
      >
        {currentScaleData?.label}
      </motion.div>

      {/* Increase Font Size Button */}
      <motion.button
        onClick={increaseFontSize}
        disabled={!canIncrease()}
        className="font-control-button"
        aria-label="Increase font size"
        title={`Increase font size${!canIncrease() ? ' (maximum reached)' : ''}`}
        whileHover={canIncrease() ? {
          scale: [null, 1.1, 1.6],
          transition: {
            duration: 0.3,
            times: [0, 0.6, 1],
            ease: ["easeInOut", "easeOut"],
          },
        } : {}}
        whileTap={canIncrease() ? {
          scale: 0.95,
          transition: { duration: 0.1 }
        } : {}}
      >
        <FontSizeIncreaseIcon className="w-6 h-6" />
      </motion.button>

      {/* Hidden reset button for development/testing */}
      {process.env.NODE_ENV === 'development' && (
        <motion.button
          onClick={resetFontSize}
          className="font-control-button opacity-50 hover:opacity-100"
          aria-label="Reset font size to default"
          title="Reset to default font size"
          style={{ marginLeft: '0.5rem' }}
        >
          <span className="text-xs">R</span>
        </motion.button>
      )}
    </div>
  );
};

export default FontSizeControls;